import {FancyPage} from '@wandb/weave/components/FancyPage';
import {useProjectHasTraceServerData} from '@wandb/weave/components/PagePanelComponents/Home/Browse3/pages/wfReactInterface/context';
import React, {memo, useCallback, useContext, useEffect, useRef} from 'react';
import {Redirect, Route, RouteProps, Switch, useRouteMatch} from 'react-router';
import {useHistory} from 'react-router-dom';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';

import LaunchPage from '../../components/Launch';
import {usePublicQueueAccess} from '../../components/Launch/hooks';
import {JobsPage} from '../../components/Launch/Jobs/JobsPage';
import {useAccountOrganizationOrPersonalEntityUsingLaunch} from '../../components/Launch/launchActive';
import {TriggersPage} from '../../components/Launch/Triggers/TriggersPage';
import NoMatch from '../../components/NoMatch';
import ProjectArtifactsTab from '../../components/ProjectArtifactsTab';
import ProjectOverview from '../../components/ProjectOverview/ProjectOverview';
import {ProjectWeaveTab} from '../../components/ProjectWeaveTab';
import {
  QuickStart,
  QuickStartAnalyticsOrigin,
} from '../../components/QuickStart';
import {checkRegistryProject} from '../../components/Registries/common/utils';
import {IMPORT_PANEL_QS} from '../../components/ReportPanelGridSection';
import ReportTableWithQuery from '../../components/ReportTable';
import {useRunDetailsTo} from '../../components/RunDetails/hooks';
import {AccountSelectorContext} from '../../components/Search/SearchNav/AccountSelectorContextProvider';
import {getNodeFromValue} from '../../components/Slate/SlashMenu';
import SweepsTable from '../../components/SweepsTable';
import {envIsLocal, envIsPublicCloud, hasTraceBackend} from '../../config';
import {LoaderProxy} from '../../containers/AppLoader';
import {
  PROJECT_BASE_PATH,
  PROJECT_WORKSPACE_PATHS,
  RUN_CODE_FILES_PATH,
  RUN_FILES_PATH,
  RUN_PATHS,
  RUN_WORKSPACE_PATH,
  RUN_WORKSPACE_PATHS,
  RUNS_TAB_PATH,
  WEAVE_SLUG,
} from '../../routes/paths';
import {Analytics} from '../../services/analytics';
import * as PollingActions from '../../state/polling/actions';
import {useViewer} from '../../state/viewer/hooks';
import {useIsAdminModeActiveAndIsNotLocalEnv} from '../../util/admin';
import {
  getDatadogRum,
  usePersistentRumViewContext,
} from '../../util/datadog/datadogRum';
import {usePerformanceStatLogger} from '../../util/datadog/usePerformanceStatLogger';
import {AccessOption, getProjectAccess} from '../../util/permissions';
import {
  useStatsigGateSystemMetricsQueryCap,
  useStatsigGateWorkspaceConsolidation,
} from '../../util/rampFeatureFlags';
import * as Report from '../../util/report';
import {useViewerHasRestrictedProjectView} from '../../util/restrictedProjectView';
import * as urls from '../../util/urls';
import {useModelsAccess} from '../../util/useModelsAccess';
import {useWeaveAccess} from '../../util/useWeaveAccess';
import {checkTheme, getTheme} from '../Benchmark/Theme';
import {InferenceProvider} from '../InferenceContext';
import JobPage from '../JobPage';
import NoAccessPage from '../NoAccessPage';
import {DEFAULT_ONPREM_ORGANIZATION_NAME} from '../OrgDashboard/OrgRoles/RolesDashboard';
import {NoRunsSampleScript} from '../ProjectPageOnboarding';
import {ProjectPageContextProvider} from './ProjectPageContext';
import {RunsRedirect} from './RunsRedirect';
import {shouldRedirectToWeaveTraces} from './shouldRedirectToWeaveTraces';
import {useDocumentMeta} from './useDocumentMeta';
import {useProjectPageProps} from './useProjectPageProps';
import {useProjectParams} from './useProjectParams';
import {useProjectSidebar} from './useProjectSidebar';
import {WorkspaceTabBody} from './WorkspaceTabBody';
const StyledProjectPage = styled.div<{hideSidebar?: boolean}>`
  & > div.fancy-page > div.fancy-page__sidebar {
    ${props => (props.hideSidebar ? 'width: 0px; overflow: hidden;' : '')}
    transition: width 0.2s linear;
  }
`;
StyledProjectPage.displayName = 'StyledProjectPage';

const MIN_POLL_INTERVAL = 10000;
const MAX_POLL_INTERVAL = 60000;
interface RouteWithAccessProps extends RouteProps {
  children: React.ReactNode;
  isPublicOrOpenProject: boolean;
}
const RouteWithModelsAccess: React.FC<RouteWithAccessProps> = ({
  children,
  isPublicOrOpenProject,
  ...props
}) => {
  const {hasModelsAccess, isOrgMemberLoading} = useModelsAccess(
    isPublicOrOpenProject
  );
  if (!isOrgMemberLoading && !hasModelsAccess) {
    return <NoAccessPage contentKey={'noModelsAccess'} />;
  }

  return <Route {...props}>{children}</Route>;
};

const RouteWithWeaveAccess: React.FC<RouteWithAccessProps> = ({
  children,
  isPublicOrOpenProject,
  ...props
}) => {
  const isAdminMode = useIsAdminModeActiveAndIsNotLocalEnv();
  const {entityName, weaveTab} = useProjectParams();
  const {
    isWeaveLimitReached,
    isWeaveEnterpriseTrialExpired,
    hasWeaveAccess,
    isLoading,
  } = useWeaveAccess(isPublicOrOpenProject, isAdminMode, entityName);

  useEffect(() => {
    try {
      const datadogRum = getDatadogRum();
      // This format (`<ProductArea>[:<TableName>]`) matches the
      // format used by the rest of the Model's product line. See
      // `TrackRouteComponent` for similar logic
      const viewName = weaveTab ? 'Weave:' + weaveTab : 'Weave';
      datadogRum.setViewName(viewName);
    } catch (error) {
      console.error(error);
    }
  }, [weaveTab]);

  if (isLoading) {
    return <LoaderProxy name="project-page" />;
  }
  if (isWeaveLimitReached) {
    return <NoAccessPage contentKey={'weaveLimitReached'} />;
  }
  if (isWeaveEnterpriseTrialExpired) {
    return <NoAccessPage contentKey={'weaveTrialExpired'} />;
  }
  if (!hasWeaveAccess) {
    return <NoAccessPage contentKey={'noWeaveAccess'} />;
  }
  return <Route {...props}>{children}</Route>;
};

const ProjectPageComp = () => {
  const {dispatch, projectQuery, pushDraftReport} = useProjectPageProps();
  const isInRunDetails = useRouteMatch(RUN_PATHS) != null;
  const isInRunWorkspacePath = useRouteMatch(RUN_WORKSPACE_PATH) != null;
  const history = useHistory();
  const {
    artifactTypeName,
    artifactCollectionName,
    artifactCommitHash,
    artifactCollectionView,
    artifactTab,
    entityName,
    launchTab,
    filePath,
    projectName,
    tab,
    weaveTab,
    runName,
  } = useProjectParams();

  const projectWorkspaceRedirectUrl = useRunDetailsTo(runName ?? '');

  const projectHasTraceServerData = useProjectHasTraceServerData(
    entityName,
    projectName
  );

  /**
   * This is to pre-hit the statsig gate so that we have a value cached in advance of the panels loading in the workspace. Since the gate is about NOT running queries we want to delay, I want to be sure a query doesn't fire off on panel mount if the feature gate hasn't resolved to a value.
   */
  useStatsigGateSystemMetricsQueryCap(entityName);

  const hasWeaveData = projectHasTraceServerData.result;

  const pollIntervalSet = useRef(false);

  useEffect(() => {
    if (
      !projectQuery.loading &&
      projectQuery.data?.project !== null &&
      !pollIntervalSet.current
    ) {
      // Scale poll interval from 10s (when there are 0 runs) to 60s
      // (when there are 5000 runs).
      let pollInterval =
        (((projectQuery.data?.project.totalRuns ?? 0) / 5000) * 50 + 10) * 1000;
      if (pollInterval < MIN_POLL_INTERVAL) {
        pollInterval = MIN_POLL_INTERVAL;
      }
      if (pollInterval > MAX_POLL_INTERVAL) {
        pollInterval = MAX_POLL_INTERVAL;
      }
      dispatch(PollingActions.setBasePollInterval(pollInterval));
      pollIntervalSet.current = true;
    }
  });

  // Clear polling on unmount
  useEffect(() => {
    if (!pollIntervalSet.current) {
      return;
    }
    return () => {
      dispatch(PollingActions.setBasePollInterval(0));
    };
  }, []); // eslint-disable-line

  const {selectedAccount} = useContext(AccountSelectorContext);
  const accountOrgName = envIsPublicCloud
    ? (selectedAccount?.name ?? '')
    : DEFAULT_ONPREM_ORGANIZATION_NAME;

  const project = !projectQuery.loading ? projectQuery.data?.project : null;
  const runCount = project?.totalRuns ?? 0;
  const hasModelsData = runCount > 0;
  const isLoading = projectQuery.loading || projectHasTraceServerData.loading;

  const viewingRestricted = useViewerHasRestrictedProjectView({
    projectAccess: getProjectAccess(project?.access ?? AccessOption.Private),
    projectName,
    entityName,
  });

  const isPublicOrOpenProject =
    project?.access === AccessOption.Public ||
    project?.access === AccessOption.Open;
  const {hasModelsAccess, isOrgMemberLoading} = useModelsAccess(
    isPublicOrOpenProject
  );

  const {isLaunchActive} = useAccountOrganizationOrPersonalEntityUsingLaunch();
  // Check this feature flag here instead of inside of the
  // useAccountOrganizationOrPersonalEntityUsingLaunch hook because we want to
  // show the Jobs tab on the project page, but not the Launch application in the workspace.
  const {hasPublicQueueAccess, isFeatureFlagLoading} = usePublicQueueAccess();
  const viewer = useViewer();
  const isWandbAdmin = !envIsLocal && (viewer?.admin ?? false);

  const sidebarItems = useProjectSidebar(
    isLoading,
    viewingRestricted,
    hasModelsData,
    hasWeaveData,
    hasTraceBackend,
    hasModelsAccess,
    isLaunchActive || (hasPublicQueueAccess && !isFeatureFlagLoading),
    isWandbAdmin
  );

  useDocumentMeta({
    reportCount: project?.reports?.totalCount ?? 0,
    runCount,
    sweepCount: project?.totalSweeps ?? 0,
    isInRunDetails,
  });

  usePerformanceStatLogger(
    'workspace.runs_count',
    project?.totalRuns ?? 0,
    v => v >= 500
  );
  usePersistentRumViewContext(
    'workspace.runs_count',
    project?.totalRuns ?? null
  );

  const baseUrl = urls.project({entityName, name: projectName});

  const onSetTableExpanded = useCallback(
    (isTableExpanded: boolean) => {
      history.push({
        pathname: baseUrl + (isTableExpanded ? '/table' : '/workspace'),
        search: history.location.search,
      });
    },
    [baseUrl, history]
  );

  const isWorkspaceConsolidationEnabled =
    useStatsigGateWorkspaceConsolidation();

  if (isLoading || isOrgMemberLoading) {
    return <LoaderProxy name="project-page" />;
  }

  const {isRegistryProject, registryName} = checkRegistryProject(projectName);
  if (isRegistryProject) {
    return <Redirect to={urls.globalRegistry(accountOrgName, registryName)} />;
  }

  if (shouldRedirectToWeaveTraces(tab, hasWeaveData, hasModelsData)) {
    return (
      <Redirect
        to={urls.weaveView({entityName, projectName, weaveTab: 'traces'})}
      />
    );
  }

  if (!project) {
    return <NoMatch />;
  }

  // Team admins can view some details of restricted projects if they are not explicit members. Redirect them to the
  // project overview page, where they can view project members and add themselves
  if (viewingRestricted && tab !== 'overview') {
    return <Redirect to={urls.projectOverview(entityName, projectName)} />;
  }

  if (project.entity.claimingEntity != null) {
    // If the project's entity has been claimed, redirect to the claiming
    // entity instead so the original link still works.
    return (
      <Redirect
        to={urls.project({
          entityName: project.entity.claimingEntity.name,
          name: projectName,
        })}
      />
    );
  }

  if (
    project.isBenchmark &&
    checkTheme({entityName, name: projectName, views: project.views})
  ) {
    return (
      <Redirect to={urls.benchmarkProject({entityName, name: projectName})} />
    );
  }

  if (isInRunWorkspacePath) {
    return <Redirect to={projectWorkspaceRedirectUrl()} />;
  }

  const linkedBenchmarkMeta =
    !project.isBenchmark && project.linkedBenchmark != null
      ? project.linkedBenchmark
      : null;

  const linkedBenchmarkInfo =
    linkedBenchmarkMeta != null ? getTheme(linkedBenchmarkMeta) : null;

  if (project.totalRuns === 0 && projectName === 'sample-project') {
    return <NoRunsSampleScript key="runsTab" />;
  }

  let activeSlug = tab;
  if (weaveTab) {
    if ('calls' === weaveTab) {
      activeSlug = `${tab}/traces`;
    } else if ('ops' === weaveTab) {
      activeSlug = `${tab}/operations`;
    } else {
      activeSlug = `${tab}/${weaveTab}`;
    }
  } else if (tab === 'runs') {
    // Slightly confusing, but if `tab` URL param is "runs", we're actually looking
    // at a workspace in single-run mode. For the "Runs" sidebar item (i.e. expanded
    // runs table), the `tab` URL param value is actually "table".
    activeSlug = 'workspace';
  }

  return (
    <ProjectPageContextProvider project={project}>
      <StyledProjectPage className="project-page">
        <FancyPage
          baseUrl={baseUrl}
          activeSlug={activeSlug ?? 'workspace'}
          items={sidebarItems}>
          <Switch>
            <Route
              path={[
                `${PROJECT_BASE_PATH}/overview`,
                `${PROJECT_BASE_PATH}/overview/details`,
                `${PROJECT_BASE_PATH}/overview/users`,
                `${PROJECT_BASE_PATH}/overview/providers`,
                `${PROJECT_BASE_PATH}/overview/weave`,
              ]}>
              <ProjectOverview
                project={project}
                hasWeaveData={hasWeaveData}
                hasModelsData={hasModelsData}
              />
            </Route>
            <RouteWithModelsAccess
              path={`${PROJECT_BASE_PATH}/runs`}
              exact
              isPublicOrOpenProject={isPublicOrOpenProject}>
              <RunsRedirect />
            </RouteWithModelsAccess>
            <RouteWithModelsAccess
              path={
                isWorkspaceConsolidationEnabled
                  ? [
                      ...PROJECT_WORKSPACE_PATHS,
                      ...RUN_WORKSPACE_PATHS,
                      `${PROJECT_BASE_PATH}/table`,
                      RUNS_TAB_PATH,
                      RUN_FILES_PATH,
                      RUN_CODE_FILES_PATH,
                    ]
                  : [...PROJECT_WORKSPACE_PATHS, `${PROJECT_BASE_PATH}/table`]
              }
              exact
              isPublicOrOpenProject={isPublicOrOpenProject}>
              {runCount === 0 ? (
                <QuickStart
                  analyticsOrigin={QuickStartAnalyticsOrigin.PROJECT_PAGE}
                />
              ) : (
                <WorkspaceTabBody
                  isTableExpanded={tab === 'table'}
                  linkedBenchmarkInfo={linkedBenchmarkInfo}
                  onSetTableExpanded={onSetTableExpanded}
                  runCount={runCount}
                />
              )}
            </RouteWithModelsAccess>
            <RouteWithModelsAccess
              path={`${PROJECT_BASE_PATH}/launch`}
              isPublicOrOpenProject={isPublicOrOpenProject}>
              <LaunchPage
                tab={launchTab}
                projectLoading={projectQuery.loading}
                entityName={entityName}
                project={project}
                refetchProject={projectQuery.refetch}
              />
            </RouteWithModelsAccess>
            <RouteWithModelsAccess
              path={[
                `${PROJECT_BASE_PATH}/jobs/:jobId/:jobTab/:jobVersion?`,
                `${PROJECT_BASE_PATH}/jobs/:jobId/:jobTab?`,
              ]}
              isPublicOrOpenProject={isPublicOrOpenProject}>
              <JobPage />
            </RouteWithModelsAccess>
            <RouteWithModelsAccess
              path={`${PROJECT_BASE_PATH}/jobs`}
              exact
              isPublicOrOpenProject={isPublicOrOpenProject}>
              <JobsPage entityName={entityName} projectName={projectName} />
            </RouteWithModelsAccess>
            <RouteWithModelsAccess
              path={`${PROJECT_BASE_PATH}/automations`}
              isPublicOrOpenProject={isPublicOrOpenProject}>
              <TriggersPage entityName={entityName} project={project} />
            </RouteWithModelsAccess>
            <RouteWithModelsAccess
              path={`${PROJECT_BASE_PATH}/sweeps`}
              isPublicOrOpenProject={isPublicOrOpenProject}>
              <SweepsTable entityName={entityName} projectName={projectName} />
            </RouteWithModelsAccess>
            <RouteWithModelsAccess
              path={`${PROJECT_BASE_PATH}/reportlist`}
              isPublicOrOpenProject={isPublicOrOpenProject}>
              <ReportTableWithQuery
                entityName={entityName}
                projectName={projectName}
                runCount={runCount}
                onCreateNewReport={() => {
                  Analytics.track('Create Report Started', {
                    location: `reports tab`,
                    entityName,
                    projectName,
                  });
                  const report = Report.getEmptyReportConfig();
                  pushDraftReport(report);
                }}
                onCreateReportPrompt={() => {
                  Analytics.track('Create Report Started', {
                    location: `create report prompt reports tab`,
                    entityName,
                    projectName,
                  });
                  const report = Report.getEmptyReportConfig([
                    getNodeFromValue(`panel-grid`),
                  ]);
                  pushDraftReport(report, undefined, {
                    redirectQS: {[IMPORT_PANEL_QS]: null},
                  });
                }}
              />
            </RouteWithModelsAccess>
            <RouteWithModelsAccess
              path={`${PROJECT_BASE_PATH}/artifacts`}
              isPublicOrOpenProject={isPublicOrOpenProject}>
              <ProjectArtifactsTab
                entityName={entityName}
                projectName={projectName}
                history={history}
                artifactTypeName={artifactTypeName}
                artifactCollectionName={artifactCollectionName}
                artifactCommitHash={artifactCommitHash}
                artifactCollectionView={artifactCollectionView}
                artifactTab={artifactTab}
                filePath={filePath}
              />
            </RouteWithModelsAccess>
            <RouteWithWeaveAccess
              path={`${PROJECT_BASE_PATH}/${WEAVE_SLUG}`}
              isPublicOrOpenProject={isPublicOrOpenProject}>
              <InferenceProvider>
                <ProjectWeaveTab
                  entityName={entityName}
                  projectName={projectName}
                />
              </InferenceProvider>
            </RouteWithWeaveAccess>
          </Switch>
        </FancyPage>
      </StyledProjectPage>
    </ProjectPageContextProvider>
  );
};

export const ProjectPage = memo(ProjectPageComp);
